import { useState, type FormEvent } from "react";
import { ArrowRight } from "lucide-react";
import { usePage, type SectionProps } from "@/flash/context";
import { Honeypot, arr, cn, isBot, postForm, rv, str, strs, tel } from "@/flash/ui";
import { Kicker, MaskTitle, Shell } from "./kit";

const FIELD =
  "sx-field box-border h-12 w-full rounded-[3px] border border-white/[.12] bg-white/[.02] px-3.5 text-[15px] text-foreground placeholder:text-muted-foreground/60 transition-colors hover:border-white/25 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60";

const FIELD_TYPES = new Set(["text", "email", "tel", "date", "time", "number"]);

/** Contact as an integrated technical panel. Submission logic is identical to the original form. */
export default function SysContact({ title, subtitle, fields, submit, phone, note }: SectionProps) {
  const { formUrl, ui } = usePage();
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [errMsg, setErrMsg] = useState("");
  const list = arr(fields).slice(0, 5);
  const call = tel(phone);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (state === "sending") return;
    const form = e.currentTarget;
    if (isBot(form)) {
      setState("done");
      return;
    }
    // No receiving endpoint configured: never fake success.
    if (!formUrl) {
      setErrMsg("");
      setState("error");
      return;
    }
    setState("sending");
    try {
      const data = new FormData(form);
      const body = new URLSearchParams();
      list.forEach((f, i) => {
        const key = str(f.type).toLowerCase() === "email" ? "email" : str(f.label) || `field${i + 1}`;
        body.set(key, str(data.get(`f${i}`)));
      });
      const err = await postForm(formUrl, body);
      if (err !== null) {
        setErrMsg(err);
        setState("error");
        return;
      }
      form.reset();
      setState("done");
    } catch {
      setErrMsg("");
      setState("error");
    }
  };

  return (
    <section id="contact" className="relative pb-24 pt-12 md:pb-36 md:pt-16">
      <Shell className="grid gap-12 border-t border-white/[.08] pt-14 md:pt-20 lg:grid-cols-12 lg:gap-8">
        <div className="flex flex-col gap-6 lg:col-span-5">
          <Kicker>Contact</Kicker>
          <MaskTitle className="sx-display-sm max-w-sm">{str(title)}</MaskTitle>
          {subtitle ? <p {...rv(1)} className="max-w-sm text-[15px] leading-relaxed text-muted-foreground">{str(subtitle)}</p> : null}
          {call ? (
            <a href={call} className="mt-2 w-fit border-b border-white/15 pb-1.5 text-[17px] tabular-nums transition-colors hover:border-primary hover:text-primary">
              {str(phone)}
            </a>
          ) : null}
          {note ? <p className="text-[13px] text-muted-foreground">{str(note)}</p> : null}
        </div>

        <div {...rv(1)} className="min-w-0 lg:col-span-7">
          <div className="rounded-[4px] border border-white/[.08] bg-white/[.015]">
            <form id="contact-form" onSubmit={onSubmit} aria-busy={state === "sending"} data-readdy-form className="grid gap-x-5 gap-y-6 p-5 sm:grid-cols-2 md:p-8">
              {list.map((f, i) => {
                const kind = str(f.type).toLowerCase();
                const options = strs(f.options).slice(0, 12);
                const common = { id: `c-${i}`, name: `f${i}` };
                const wide = kind === "textarea" || kind === "select";
                return (
                  <div key={i} className={cn("grid min-w-0 gap-2.5", wide && "sm:col-span-2")}>
                    <label htmlFor={`c-${i}`} className="text-[13px] leading-none text-foreground/75">
                      {str(f.label)}
                    </label>
                    {kind === "select" && options.length > 0 ? (
                      <select {...common} required defaultValue="" className={cn(FIELD, "cursor-pointer")}>
                        <option value="" disabled>
                          {str(f.placeholder)}
                        </option>
                        {options.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </select>
                    ) : kind === "textarea" ? (
                      <textarea {...common} rows={4} maxLength={500} placeholder={str(f.placeholder)} className={cn(FIELD, "h-auto min-h-28 resize-y py-3")} />
                    ) : (
                      <input {...common} type={FIELD_TYPES.has(kind) ? kind : "text"} placeholder={str(f.placeholder)} required className={FIELD} />
                    )}
                  </div>
                );
              })}
              <Honeypot />
              <div className="flex flex-col gap-3 pt-2 sm:col-span-2">
                <button
                  type="submit"
                  disabled={state === "sending"}
                  className="sx-submit group inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[3px] bg-primary text-[15px] font-medium text-primary-foreground transition duration-200 hover:brightness-110 disabled:cursor-wait disabled:opacity-70"
                >
                  {str(submit)}
                  {state === "sending" ? (
                    <span className="sx-spin size-4 rounded-full border-2 border-current border-e-transparent" aria-hidden />
                  ) : (
                    <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  )}
                </button>
                <p
                  role="status"
                  key={state}
                  aria-live="polite"
                  className={cn(
                    "sx-status border-s-2 ps-3 text-sm",
                    state === "error" ? "border-red-400 text-red-300" : "border-primary text-primary",
                    state === "idle" || state === "sending" ? "hidden" : "",
                  )}
                >
                  {state === "done" ? ui.form_success : state === "error" ? errMsg || ui.form_error : ""}
                </p>
              </div>
            </form>
          </div>
        </div>
      </Shell>
    </section>
  );
}